import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * TooltipArrow Component
 * A reusable tooltip with an arrow that appears on hover
 * @param {string|React.ReactNode} text - The tooltip text to display (can be string or JSX)
 * @param {"above"|"below"|"left"|"right"} [position="above"] - Placement relative to the trigger
 * @param {"center"|"end"} [align="center"] - Horizontal alignment for above/below placements
 * @param {string} className - Additional classes for the tooltip container
 * @param {string} linkText - Optional text for a clickable link (appears after main text)
 * @param {function} onLinkClick - Optional click handler for the link
 * @param {string} buttonText - Optional text for a button (appears as a block button below content)
 * @param {function} onButtonClick - Optional click handler for the button
 * @param {string} buttonClassName - Optional additional classes for the button
 * @param {boolean} usePortal - If true, renders tooltip via Portal to escape overflow containers
 * @param {number} [closeDelay=100] - ms to wait before hiding after mouse leave (portal mode)
 */
const TooltipArrow = ({
  text,
  position = "above",
  align = "center",
  className = "",
  linkText = "",
  onLinkClick = null,
  buttonText = "",
  onButtonClick = null,
  buttonClassName = "",
  usePortal = false,
  closeDelay = 100,
}) => {
  const isAbove = position === "above";
  const isBelow = position === "below";
  const isLeft = position === "left";
  const isRight = position === "right";
  const alignEnd = align === "end";
  const [isVisible, setIsVisible] = useState(false);
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  // If there's a button, we need to allow wrapping and set min-width
  const hasButton = buttonText && onButtonClick;
  const whitespaceClass = hasButton ? "whitespace-normal" : "whitespace-nowrap";
  const minWidthClass = hasButton ? "min-w-[200px]" : "";

  // Handlers for showing/hiding with delay (for portal mode)
  /**
   * Shows the tooltip and cancels any pending hide timeout.
   *
   * @returns {void}
   */
  const handleMouseEnter = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsVisible(true);
  };

  /**
   * Hides the tooltip immediately or after `closeDelay`.
   *
   * @returns {void}
   */
  const handleMouseLeave = () => {
    // Optional delay lets the cursor move onto an interactive tooltip.
    if (closeDelay <= 0) {
      setIsVisible(false);
      return;
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, closeDelay);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Update portal position when visible
  useEffect(() => {
    if (usePortal && isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipHeight = tooltipRef.current?.offsetHeight || 40;
      const gap = 8;

      if (isLeft) {
        setPortalPosition({
          top: rect.top + rect.height / 2,
          left: rect.left - gap,
        });
        return;
      }

      if (isRight) {
        setPortalPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + gap,
        });
        return;
      }

      setPortalPosition({
        top: isAbove ? rect.top - tooltipHeight - gap : rect.bottom + gap,
        // Center on trigger, or pin tooltip's right edge to the trigger's right edge.
        left: alignEnd ? rect.right : rect.left + rect.width / 2,
      });
    }
  }, [usePortal, isVisible, isAbove, isLeft, isRight, alignEnd]);

  let arrowClasses = "";
  if (isLeft) {
    // Arrow on the tooltip's right edge, pointing toward the pill.
    arrowClasses =
      "absolute top-1/2 -translate-y-1/2 left-full w-0 h-0 border-t-4 border-b-4 border-l-4 border-transparent border-l-gray-100";
  } else if (isRight) {
    arrowClasses =
      "absolute top-1/2 -translate-y-1/2 right-full w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-100";
  } else {
    const arrowAlignClasses = alignEnd
      ? "right-3"
      : "left-1/2 transform -translate-x-1/2";
    arrowClasses = isAbove
      ? `absolute top-full ${arrowAlignClasses} w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-100`
      : `absolute bottom-full ${arrowAlignClasses} w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-100`;
  }

  const tooltipContent = (
    <>
      {text}
      {linkText && onLinkClick && (
        <>
          {" "}
          <button
            onClick={onLinkClick}
            className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
          >
            {linkText}
          </button>
        </>
      )}
      {buttonText && onButtonClick && (
        <button
          onClick={onButtonClick}
          className={`block mt-1 w-full px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors ${buttonClassName}`}
        >
          {buttonText}
        </button>
      )}
      {/* Arrow pointing to the parent element */}
      <div className={arrowClasses}></div>
    </>
  );

  let portalTransformClass = alignEnd ? "-translate-x-full" : "-translate-x-1/2";
  if (isLeft) {
    portalTransformClass = "-translate-x-full -translate-y-1/2";
  } else if (isRight) {
    portalTransformClass = "-translate-y-1/2";
  }

  // Portal-based tooltip (escapes overflow containers)
  if (usePortal) {
    return (
      <>
        {/* Invisible trigger area to capture hover */}
        <div
          ref={triggerRef}
          className="absolute inset-0"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
        {isVisible &&
          createPortal(
            <div
              ref={tooltipRef}
              className={`fixed z-[9999] font-inter shadow-lg shadow-gray-700 text-base px-2 py-1 rounded text-xs text-center ${whitespaceClass} ${minWidthClass} transform ${portalTransformClass} pointer-events-auto bg-gray-100 ${className}`}
              style={{
                top: `${portalPosition.top}px`,
                left: `${portalPosition.left}px`,
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {tooltipContent}
            </div>,
            document.body
          )}
      </>
    );
  }

  // Standard absolute-positioned tooltip (original behavior)
  let horizontalClasses = alignEnd
    ? "right-0"
    : "left-1/2 transform -translate-x-1/2";
  let positionClasses = isAbove ? "bottom-full mb-2" : "top-full mt-2";

  if (isLeft) {
    horizontalClasses = "right-full mr-2 top-1/2 -translate-y-1/2";
    positionClasses = "";
  } else if (isRight) {
    horizontalClasses = "left-full ml-2 top-1/2 -translate-y-1/2";
    positionClasses = "";
  } else if (isBelow) {
    positionClasses = "top-full mt-2";
  }

  const baseClasses = `absolute z-50 shadow-lg shadow-gray-700 text-base px-2 py-1 rounded text-xs ${whitespaceClass} ${minWidthClass} ${horizontalClasses} opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 delay-200 pointer-events-auto hover:opacity-100 hover:visible bg-gray-100`;

  return (
    <div
      className={`text-center ${baseClasses} ${positionClasses} ${className}`}
    >
      {tooltipContent}
    </div>
  );
};

export default TooltipArrow;
